import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateStudent } from '@/features/students/hooks';
import type { StudentView } from '@/features/students/types';
import { ApiError } from '@/lib/api/client';

/** `PATCH /students/:id` only accepts `guardianName`/`guardianPhone` - the actual, complete update contract. */
export function EditStudentDialog({
  student,
  open,
  onOpenChange,
}: {
  readonly student: StudentView;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const [guardianName, setGuardianName] = useState(student.guardianName ?? '');
  const [guardianPhone, setGuardianPhone] = useState(student.guardianPhone ?? '');
  const update = useUpdateStudent(student.id);

  useEffect(() => {
    if (open) {
      setGuardianName(student.guardianName ?? '');
      setGuardianPhone(student.guardianPhone ?? '');
      update.reset();
    }
    // Only re-sync when the dialog opens for this student, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, student.id]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    update.mutate(
      {
        guardianName: guardianName.trim().length > 0 ? guardianName.trim() : null,
        guardianPhone: guardianPhone.trim().length > 0 ? guardianPhone.trim() : null,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  }

  const apiErrorMessage =
    update.error instanceof ApiError
      ? update.error.message
      : update.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ubah Data Santri</DialogTitle>
          <DialogDescription>Perbarui data wali untuk santri ini.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="edit-guardian-name">Nama Wali</Label>
            <Input
              id="edit-guardian-name"
              type="text"
              value={guardianName}
              onChange={(event) => {
                setGuardianName(event.target.value);
              }}
              autoComplete="off"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="edit-guardian-phone">Telepon Wali</Label>
            <Input
              id="edit-guardian-phone"
              type="text"
              value={guardianPhone}
              onChange={(event) => {
                setGuardianPhone(event.target.value);
              }}
              autoComplete="off"
            />
          </div>

          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
